import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from '@traction/shared';
import type { LeadWithDraft } from '@traction/shared';
import { useAuth } from '@/contexts/AuthContext';
import { getLeadsWithDrafts, updateLeadStatus } from '@traction/api-client';
import { useToast } from '@/components/Toast';
import { AnimatedCounter } from '@/components/ui/AnimatedCounter';
import {
  Users,
  TrendingUp,
  ArrowRight,
  Zap,
  Target,
  Calendar,
  Send,
  MessageSquare,
  Star,
  Sparkles,
  ChevronRight,
  Trophy,
  Flame,
  ArrowUpRight,
  CheckCircle2,
  Mail,
  X,
  Edit3,
  Coffee,
} from 'lucide-react';

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function getMotivationalMessage(stats: { pending: number; sent: number; replied: number }): string {
  if (stats.replied > 0) return "You're getting responses! Keep the momentum going.";
  if (stats.sent > 5) return "Great outreach today! Replies usually come within 24-48 hours.";
  if (stats.pending > 0) return "Fresh leads are waiting. Let's make some connections!";
  return "Ready to find your next customer? Let's go hunting.";
}

interface WeekDay {
  day: string;
  sent: number;
  replies: number;
}

export function DashboardPage() {
  const { user, profile } = useAuth();
  const [leads, setLeads] = useState<LeadWithDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [skippingLead, setSkippingLead] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    async function loadLeads() {
      if (!user) return;
      try {
        const allLeads = await getLeadsWithDrafts(user.uid);
        setLeads(allLeads);
      } catch (error) {
        console.error('Failed to load leads:', error);
      } finally {
        setLoading(false);
      }
    }
    loadLeads();
  }, [user]);

  // Calculate stats
  const stats = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

    const todaysLeads = leads.filter((l) => new Date(l.delivered_at) >= today);
    const pending = leads.filter((l) => l.status === 'pending');
    const contacted = leads.filter((l) => l.status === 'contacted');
    const replied = leads.filter((l) => l.status === 'replied');
    const starred = leads.filter((l) => l.is_starred);

    // This week's activity
    const weekLeads = leads.filter((l) => new Date(l.delivered_at) >= weekAgo);
    const weekSent = leads.filter(
      (l) => l.contacted_at && new Date(l.contacted_at) >= weekAgo
    );
    const weekReplied = leads.filter(
      (l) => l.replied_at && new Date(l.replied_at) >= weekAgo
    );

    // Warm leads (high score or strong signals)
    const warmSignals = ['funding', 'leadership_change', 'hiring'];
    const warm = leads.filter(
      (l) =>
        l.status === 'pending' &&
        (l.score === 'high' || (l.signal_type && warmSignals.includes(l.signal_type)))
    );

    // Reply rate
    const replyRate = contacted.length > 0
      ? Math.round((replied.length / contacted.length) * 100)
      : 0;

    // Ready to send: pending leads with email drafts and real emails (not apollo placeholders)
    const readyToSend = pending.filter((l) => l.email_draft && l.email && !l.email.includes('@apollo.local'));

    // Needs enrichment: has draft but no real email
    const needsEnrichment = pending.filter((l) => l.email_draft && (!l.email || l.email.includes('@apollo.local')));

    return {
      total: leads.length,
      todaysLeads: todaysLeads.length,
      pending: pending.length,
      contacted: contacted.length,
      replied: replied.length,
      starred: starred.length,
      warm: warm.length,
      weekLeads: weekLeads.length,
      weekSent: weekSent.length,
      weekReplied: weekReplied.length,
      replyRate,
      pendingLeads: pending.slice(0, 5),
      warmLeads: warm.slice(0, 3),
      recentReplies: replied.slice(0, 3),
      readyToSend: readyToSend.slice(0, 3), // Top 3 ready-to-send leads
      readyToSendCount: readyToSend.length,
      needsEnrichmentCount: needsEnrichment.length,
    };
  }, [leads]);

  // Handle skip action for morning briefing
  async function handleSkipLead(leadId: string) {
    setSkippingLead(leadId);
    try {
      await updateLeadStatus(leadId, 'skipped');
      setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status: 'skipped' } : l)));
      toast.success('Lead skipped');
    } catch (error) {
      console.error('Failed to skip lead:', error);
      toast.error('Failed to skip lead');
    } finally {
      setSkippingLead(null);
    }
  }

  // Generate Gmail compose URL
  function getGmailComposeUrl(lead: LeadWithDraft) {
    if (!lead.email || !lead.email_draft) return '#';
    const subject = encodeURIComponent(lead.email_draft.subject || 'Quick intro');
    const body = encodeURIComponent(lead.email_draft.body || '');
    return `https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(lead.email)}&su=${subject}&body=${body}`;
  }

  // Weekly activity data for mini chart
  const weeklyActivity = useMemo(() => {
    const days: WeekDay[] = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);

      const sent = leads.filter(
        (l) =>
          l.contacted_at &&
          new Date(l.contacted_at) >= dayStart &&
          new Date(l.contacted_at) < dayEnd
      ).length;

      const replies = leads.filter(
        (l) =>
          l.replied_at &&
          new Date(l.replied_at) >= dayStart &&
          new Date(l.replied_at) < dayEnd
      ).length;

      days.push({ day: dayNames[date.getDay()], sent, replies });
    }

    return days;
  }, [leads]);

  const maxActivity = Math.max(...weeklyActivity.map((d) => d.sent + d.replies), 1);

  if (loading) {
    return (
      <div className="min-h-screen p-4 sm:p-6 lg:p-8">
        {/* Skeleton Hero */}
        <div className="mb-8">
          <div className="h-8 w-48 rounded bg-cacao-dark-tertiary shimmer" />
          <div className="mt-2 h-4 w-72 rounded bg-cacao-dark-tertiary shimmer" />
        </div>

        {/* Skeleton Stats Grid */}
        <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className={`rounded-xl border border-cacao-dark-tertiary bg-cacao-dark-secondary p-5 slide-up stagger-${i}`}>
              <div className="flex items-center justify-between">
                <div className="h-11 w-11 rounded-lg bg-cacao-dark-tertiary shimmer" />
                <div className="h-4 w-12 rounded bg-cacao-dark-tertiary shimmer" />
              </div>
              <div className="mt-4 h-8 w-16 rounded bg-cacao-dark-tertiary shimmer" />
              <div className="mt-2 h-4 w-24 rounded bg-cacao-dark-tertiary shimmer" />
            </div>
          ))}
        </div>

        {/* Skeleton Content */}
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-xl border border-cacao-dark-tertiary bg-cacao-dark-secondary p-6 h-48 shimmer" />
            <div className="rounded-xl border border-cacao-dark-tertiary bg-cacao-dark-secondary p-6 h-64 shimmer" />
          </div>
          <div className="space-y-6">
            <div className="rounded-xl border border-cacao-dark-tertiary bg-cacao-dark-secondary p-6 h-48 shimmer" />
            <div className="rounded-xl border border-cacao-dark-tertiary bg-cacao-dark-secondary p-6 h-32 shimmer" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8">
      {/* Hero Section */}
      <div className="mb-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-white">
              {getGreeting()}
              {profile?.full_name ? `, ${profile.full_name.split(' ')[0]}` : ''}
            </h1>
            <p className="mt-1 text-cacao-muted">
              {getMotivationalMessage({ pending: stats.pending, sent: stats.contacted, replied: stats.replied })}
            </p>
          </div>
          <div className="flex gap-3">
            <Link
              to={ROUTES.LEADS}
              className="inline-flex items-center gap-2 rounded-lg bg-cacao-gold px-5 py-2.5 text-sm font-semibold text-cacao-dark shadow-lg shadow-cacao-gold/20 transition-all hover:bg-cacao-gold-hover hover:shadow-cacao-gold/30"
            >
              <Sparkles className="h-4 w-4" />
              Review Leads
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Pending Leads */}
        <div className="group relative overflow-hidden rounded-xl border border-cacao-dark-tertiary bg-gradient-to-br from-cacao-dark-secondary to-cacao-dark p-5 transition-all hover:border-cacao-gold/30 hover:-translate-y-1 hover:shadow-lg hover:shadow-cacao-gold/5 slide-up stagger-1">
          <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-cacao-gold/5 blur-2xl transition-all group-hover:bg-cacao-gold/10" />
          <div className="relative">
            <div className="flex items-center justify-between">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-cacao-gold/10">
                <Target className="h-5 w-5 text-cacao-gold" />
              </div>
              {stats.pending > 0 && (
                <span className="flex items-center gap-1 text-xs font-medium text-green-400">
                  <ArrowUpRight className="h-3 w-3" />
                  Ready
                </span>
              )}
            </div>
            <p className="mt-4 text-3xl font-bold text-white">
              <AnimatedCounter value={stats.pending} duration={800} />
            </p>
            <p className="mt-1 text-sm text-cacao-muted">Leads to review</p>
          </div>
        </div>

        {/* Sent This Week */}
        <div className="group relative overflow-hidden rounded-xl border border-cacao-dark-tertiary bg-gradient-to-br from-cacao-dark-secondary to-cacao-dark p-5 transition-all hover:border-blue-500/30 hover:-translate-y-1 hover:shadow-lg hover:shadow-blue-500/5 slide-up stagger-2">
          <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-blue-500/5 blur-2xl transition-all group-hover:bg-blue-500/10" />
          <div className="relative">
            <div className="flex items-center justify-between">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-blue-500/10">
                <Send className="h-5 w-5 text-blue-400" />
              </div>
              <span className="text-xs text-cacao-muted">This week</span>
            </div>
            <p className="mt-4 text-3xl font-bold text-white">
              <AnimatedCounter value={stats.weekSent} duration={800} />
            </p>
            <p className="mt-1 text-sm text-cacao-muted">Emails sent</p>
          </div>
        </div>

        {/* Replies */}
        <div className="group relative overflow-hidden rounded-xl border border-cacao-dark-tertiary bg-gradient-to-br from-cacao-dark-secondary to-cacao-dark p-5 transition-all hover:border-green-500/30 hover:-translate-y-1 hover:shadow-lg hover:shadow-green-500/5 slide-up stagger-3">
          <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-green-500/5 blur-2xl transition-all group-hover:bg-green-500/10" />
          <div className="relative">
            <div className="flex items-center justify-between">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-green-500/10">
                <MessageSquare className="h-5 w-5 text-green-400" />
              </div>
              {stats.weekReplied > 0 && (
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-green-500/20 text-xs font-bold text-green-400">
                  {stats.weekReplied}
                </span>
              )}
            </div>
            <p className="mt-4 text-3xl font-bold text-white">
              <AnimatedCounter value={stats.replied} duration={800} />
            </p>
            <p className="mt-1 text-sm text-cacao-muted">Total replies</p>
          </div>
        </div>

        {/* Reply Rate */}
        <div className="group relative overflow-hidden rounded-xl border border-cacao-dark-tertiary bg-gradient-to-br from-cacao-dark-secondary to-cacao-dark p-5 transition-all hover:border-purple-500/30 hover:-translate-y-1 hover:shadow-lg hover:shadow-purple-500/5 slide-up stagger-4">
          <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-purple-500/5 blur-2xl transition-all group-hover:bg-purple-500/10" />
          <div className="relative">
            <div className="flex items-center justify-between">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-purple-500/10">
                <TrendingUp className="h-5 w-5 text-purple-400" />
              </div>
              {stats.replyRate >= 20 && (
                <span className="flex items-center gap-1 text-xs font-medium text-purple-400">
                  <Trophy className="h-3 w-3" />
                  Great!
                </span>
              )}
            </div>
            <p className="mt-4 text-3xl font-bold text-white">
              {stats.contacted > 0 ? (
                <AnimatedCounter value={stats.replyRate} duration={1000} suffix="%" />
              ) : '--'}
            </p>
            <p className="mt-1 text-sm text-cacao-muted">Reply rate</p>
          </div>
        </div>
      </div>

      {/* Morning Briefing - Ready to Send */}
      {stats.readyToSend.length > 0 && (
        <div className="mb-8">
          <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-cacao-gold to-amber-500 shadow-lg shadow-cacao-gold/20">
                <Coffee className="h-5 w-5 text-cacao-dark" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Morning Briefing</h2>
                <p className="text-sm text-cacao-muted">{stats.readyToSendCount} email{stats.readyToSendCount !== 1 ? 's' : ''} ready to send</p>
              </div>
            </div>
            <Link
              to={ROUTES.LEADS}
              className="text-sm font-medium text-cacao-gold hover:text-cacao-gold-hover transition-colors ml-13 sm:ml-0"
            >
              View all leads →
            </Link>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {stats.readyToSend.map((lead, index) => (
              <div
                key={lead.id}
                className="group relative overflow-hidden rounded-xl border border-cacao-dark-tertiary bg-cacao-dark-secondary transition-all hover:border-cacao-gold/30 hover:shadow-lg hover:shadow-cacao-gold/5"
                style={{ animation: `slide-up 0.4s ease-out ${index * 0.1}s both` }}
              >
                {/* Card Header */}
                <div className="border-b border-cacao-dark-tertiary p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {lead.photo_url ? (
                        <img
                          src={lead.photo_url}
                          alt=""
                          className="h-10 w-10 rounded-full object-cover border border-cacao-dark-tertiary"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-cacao-gold/20 text-sm font-semibold text-cacao-gold">
                          {lead.first_name[0]}{lead.last_name[0]}
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-white">{lead.first_name} {lead.last_name}</p>
                        <p className="text-xs text-cacao-muted">{lead.title}</p>
                      </div>
                    </div>
                    <span className="rounded-full bg-green-500/10 px-2 py-0.5 text-xs font-medium text-green-400">
                      Ready
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-cacao-muted">{lead.company_name}</p>
                </div>

                {/* Email Preview */}
                <div className="p-4">
                  <div className="mb-3">
                    <p className="text-xs font-medium text-cacao-muted mb-1">Subject</p>
                    <p className="text-sm text-white font-medium truncate">{lead.email_draft?.subject || 'Quick intro'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-cacao-muted mb-1">Preview</p>
                    <p className="text-sm text-cacao-muted line-clamp-3 leading-relaxed">
                      {lead.email_draft?.body?.slice(0, 150) || 'Email draft ready...'}
                      {(lead.email_draft?.body?.length || 0) > 150 && '...'}
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="border-t border-cacao-dark-tertiary p-3 flex gap-2">
                  <a
                    href={getGmailComposeUrl(lead)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-cacao-gold px-3 py-2 text-sm font-semibold text-cacao-dark hover:bg-cacao-gold-hover transition-colors"
                  >
                    <Send className="h-4 w-4" />
                    Send
                  </a>
                  <Link
                    to={`${ROUTES.LEADS}?id=${lead.id}`}
                    className="flex items-center justify-center rounded-lg border border-cacao-dark-tertiary px-3 py-2 text-sm font-medium text-cacao-muted hover:border-cacao-gold/30 hover:text-white transition-colors"
                  >
                    <Edit3 className="h-4 w-4" />
                  </Link>
                  <button
                    type="button"
                    onClick={() => handleSkipLead(lead.id)}
                    disabled={skippingLead === lead.id}
                    title="Skip this lead"
                    aria-label="Skip this lead"
                    className="flex items-center justify-center rounded-lg border border-cacao-dark-tertiary px-3 py-2 text-sm font-medium text-cacao-muted hover:border-red-500/30 hover:text-red-400 transition-colors disabled:opacity-50"
                  >
                    {skippingLead === lead.id ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-cacao-muted border-t-transparent" />
                    ) : (
                      <X className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Ready Emails - Show appropriate message */}
      {stats.readyToSend.length === 0 && stats.pending > 0 && (
        <div className={`mb-8 rounded-xl border p-6 ${
          stats.needsEnrichmentCount > 0
            ? 'border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-cacao-dark-secondary'
            : 'border-cacao-gold/20 bg-gradient-to-br from-cacao-gold/5 to-cacao-dark-secondary'
        }`}>
          <div className="flex items-center gap-4">
            <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${
              stats.needsEnrichmentCount > 0 ? 'bg-amber-500/10' : 'bg-cacao-gold/10'
            }`}>
              <Mail className={`h-6 w-6 ${stats.needsEnrichmentCount > 0 ? 'text-amber-400' : 'text-cacao-gold'}`} />
            </div>
            <div className="flex-1">
              {stats.needsEnrichmentCount > 0 ? (
                <>
                  <h3 className="font-semibold text-white">
                    {stats.needsEnrichmentCount} lead{stats.needsEnrichmentCount !== 1 ? 's' : ''} need{stats.needsEnrichmentCount === 1 ? 's' : ''} email revealed
                  </h3>
                  <p className="text-sm text-cacao-muted">
                    Draft emails are ready! Click <span className="text-amber-400 font-medium">Enrich</span> on each lead to reveal their email address.
                  </p>
                </>
              ) : (
                <>
                  <h3 className="font-semibold text-white">No emails ready yet</h3>
                  <p className="text-sm text-cacao-muted">
                    You have {stats.pending} lead{stats.pending !== 1 ? 's' : ''} waiting. Generate emails to start sending.
                  </p>
                </>
              )}
            </div>
            <Link
              to={ROUTES.LEADS}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-cacao-dark transition-colors ${
                stats.needsEnrichmentCount > 0
                  ? 'bg-amber-500 hover:bg-amber-400'
                  : 'bg-cacao-gold hover:bg-cacao-gold-hover'
              }`}
            >
              <Sparkles className="h-4 w-4" />
              {stats.needsEnrichmentCount > 0 ? 'Enrich Leads' : 'Generate Emails'}
            </Link>
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content - 2 columns */}
        <div className="space-y-6 lg:col-span-2">
          {/* Weekly Activity Chart */}
          <div className="rounded-xl border border-cacao-dark-tertiary bg-cacao-dark-secondary p-6">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="font-medium text-white">Weekly Activity</h2>
                <p className="mt-0.5 text-sm text-cacao-muted">Your outreach over the past 7 days</p>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <span className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-cacao-gold" />
                  Sent
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-green-400" />
                  Replies
                </span>
              </div>
            </div>

            <div className="flex items-end justify-between gap-2" style={{ height: '120px' }}>
              {weeklyActivity.map((day, i) => {
                const sentHeight = Math.max((day.sent / maxActivity) * 60, day.sent > 0 ? 4 : 0);
                const repliesHeight = Math.max((day.replies / maxActivity) * 60, day.replies > 0 ? 4 : 0);
                return (
                  <div key={i} className="flex flex-1 flex-col items-center gap-2 group">
                    <div className="relative flex w-full flex-col items-center justify-end gap-0.5" style={{ height: '80px' }}>
                      {/* Sent bar with animation */}
                      <div
                        className="w-full max-w-[24px] rounded-t bg-cacao-gold/80 transition-all duration-500 ease-out group-hover:bg-cacao-gold group-hover:shadow-lg group-hover:shadow-cacao-gold/20"
                        style={{
                          height: `${sentHeight}px`,
                          animation: `grow-bar 0.6s ease-out ${i * 0.1}s both`,
                        }}
                      />
                      {/* Replies bar with animation */}
                      <div
                        className="w-full max-w-[24px] rounded-b bg-green-500/80 transition-all duration-500 ease-out group-hover:bg-green-500 group-hover:shadow-lg group-hover:shadow-green-500/20"
                        style={{
                          height: `${repliesHeight}px`,
                          animation: `grow-bar 0.6s ease-out ${i * 0.1 + 0.2}s both`,
                        }}
                      />
                    </div>
                    <span className="text-xs text-cacao-muted group-hover:text-white transition-colors">{day.day}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pending Leads */}
          <div className="rounded-xl border border-cacao-dark-tertiary bg-cacao-dark-secondary">
            <div className="flex items-center justify-between border-b border-cacao-dark-tertiary p-5">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-cacao-gold/10">
                  <Users className="h-4 w-4 text-cacao-gold" />
                </div>
                <div>
                  <h2 className="font-medium text-white">Ready to Review</h2>
                  <p className="text-xs text-cacao-muted">{stats.pending} leads waiting</p>
                </div>
              </div>
              <Link
                to={ROUTES.LEADS}
                className="flex items-center gap-1 text-sm font-medium text-cacao-gold hover:text-cacao-gold-hover transition-colors"
              >
                View all
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>

            {stats.pendingLeads.length === 0 ? (
              <div className="p-8 text-center">
                {stats.total === 0 ? (
                  <>
                    {/* First-time user - no leads yet */}
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-cacao-gold/20 to-amber-500/10 animate-float">
                      <Sparkles className="h-8 w-8 text-cacao-gold" />
                    </div>
                    <p className="text-lg font-semibold text-white">Let's find your first leads!</p>
                    <p className="mt-2 text-sm text-cacao-muted max-w-xs mx-auto">
                      Click below to discover high-intent prospects matching your ideal customer profile.
                    </p>
                    <Link
                      to={ROUTES.LEADS}
                      className="mt-5 inline-flex items-center gap-2 rounded-lg bg-cacao-gold px-4 py-2 text-sm font-semibold text-cacao-dark hover:bg-cacao-gold-hover transition-colors"
                    >
                      <Zap className="h-4 w-4" />
                      Source Your First Leads
                    </Link>
                    <p className="mt-3 text-xs text-cacao-text-tertiary">
                      Takes about 30 seconds
                    </p>
                  </>
                ) : (
                  <>
                    {/* Returning user - all caught up */}
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-green-500/20 to-emerald-500/10 animate-float">
                      <CheckCircle2 className="h-8 w-8 text-green-400" />
                    </div>
                    <p className="text-lg font-semibold text-white">All caught up!</p>
                    <p className="mt-2 text-sm text-cacao-muted max-w-xs mx-auto">
                      You've reviewed all your leads. Source more or check back tomorrow for fresh opportunities.
                    </p>
                    <Link
                      to={ROUTES.LEADS}
                      className="mt-5 inline-flex items-center gap-2 rounded-lg bg-cacao-gold/10 border border-cacao-gold/30 px-4 py-2 text-sm font-medium text-cacao-gold hover:bg-cacao-gold/20 transition-colors"
                    >
                      <Sparkles className="h-4 w-4" />
                      Source more leads
                    </Link>
                  </>
                )}
              </div>
            ) : (
              <div className="divide-y divide-cacao-dark-tertiary">
                {stats.pendingLeads.map((lead) => (
                  <Link
                    key={lead.id}
                    to={`${ROUTES.LEADS}?id=${lead.id}`}
                    className="flex items-center justify-between p-4 transition-colors hover:bg-cacao-dark-tertiary/50"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold ${
                          lead.score === 'high'
                            ? 'bg-green-500/20 text-green-400'
                            : lead.score === 'medium'
                            ? 'bg-cacao-gold/20 text-cacao-gold'
                            : 'bg-cacao-dark-tertiary text-cacao-muted'
                        }`}
                      >
                        {lead.first_name[0]}
                        {lead.last_name[0]}
                      </div>
                      <div>
                        <p className="font-medium text-white">
                          {lead.first_name} {lead.last_name}
                        </p>
                        <p className="text-sm text-cacao-muted">
                          {lead.title} at {lead.company_name}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {lead.signal_type && (
                        <span className="rounded-full bg-cacao-dark-tertiary px-2.5 py-1 text-xs font-medium text-cacao-muted">
                          {lead.signal_type}
                        </span>
                      )}
                      <ArrowRight className="h-4 w-4 text-cacao-muted" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar - 1 column */}
        <div className="space-y-6">
          {/* Warm Leads Alert */}
          {stats.warm > 0 && (
            <div className="relative overflow-hidden rounded-xl border border-orange-500/30 bg-gradient-to-br from-orange-500/10 to-transparent p-5">
              {/* Animated glow effect */}
              <div className="absolute -right-4 -top-4 h-20 w-20 rounded-full bg-orange-500/20 blur-2xl animate-pulse" />
              <div className="relative flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-500/20 pulse-glow" style={{ '--glow-color': 'rgba(249, 115, 22, 0.5)' } as any}>
                  <Flame className="h-5 w-5 text-orange-400 animate-sparkle" />
                </div>
                <div>
                  <p className="font-semibold text-orange-400">{stats.warm} Hot Lead{stats.warm > 1 ? 's' : ''}</p>
                  <p className="text-sm text-cacao-muted">High priority - act fast!</p>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                {stats.warmLeads.map((lead) => (
                  <Link
                    key={lead.id}
                    to={`${ROUTES.LEADS}?id=${lead.id}`}
                    className="flex items-center justify-between rounded-lg bg-cacao-dark/50 p-3 transition-colors hover:bg-cacao-dark"
                  >
                    <div>
                      <p className="text-sm font-medium text-white">
                        {lead.first_name} {lead.last_name}
                      </p>
                      <p className="text-xs text-cacao-muted">{lead.company_name}</p>
                    </div>
                    <span className="rounded bg-orange-500/20 px-2 py-0.5 text-xs font-medium text-orange-400">
                      {lead.signal_type || 'High Score'}
                    </span>
                  </Link>
                ))}
              </div>
              <Link
                to={`${ROUTES.LEADS}?tab=warm`}
                className="mt-4 flex items-center justify-center gap-2 rounded-lg border border-orange-500/30 py-2 text-sm font-medium text-orange-400 transition-colors hover:bg-orange-500/10"
              >
                View all hot leads
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}

          {/* Quick Stats */}
          <div className="rounded-xl border border-cacao-dark-tertiary bg-cacao-dark-secondary p-5">
            <h3 className="font-medium text-white">Quick Stats</h3>
            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm text-cacao-muted">
                  <Star className="h-4 w-4 text-cacao-gold" />
                  Starred leads
                </span>
                <span className="font-semibold text-white">
                  <AnimatedCounter value={stats.starred} duration={600} />
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm text-cacao-muted">
                  <Calendar className="h-4 w-4 text-blue-400" />
                  Leads this week
                </span>
                <span className="font-semibold text-white">
                  <AnimatedCounter value={stats.weekLeads} duration={600} />
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm text-cacao-muted">
                  <Users className="h-4 w-4 text-purple-400" />
                  Total leads
                </span>
                <span className="font-semibold text-white">
                  <AnimatedCounter value={stats.total} duration={600} />
                </span>
              </div>
            </div>
          </div>

          {/* Recent Replies */}
          {stats.recentReplies.length > 0 && (
            <div className="rounded-xl border border-green-500/30 bg-gradient-to-br from-green-500/10 to-transparent p-5">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-green-400" />
                <h3 className="font-medium text-green-400">Recent Replies</h3>
              </div>
              <div className="mt-4 space-y-3">
                {stats.recentReplies.map((lead) => (
                  <Link
                    key={lead.id}
                    to={`${ROUTES.LEADS}?id=${lead.id}`}
                    className="block rounded-lg bg-cacao-dark/50 p-3 transition-colors hover:bg-cacao-dark"
                  >
                    <p className="text-sm font-medium text-white">
                      {lead.first_name} {lead.last_name}
                    </p>
                    <p className="text-xs text-cacao-muted">{lead.company_name}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Pro Tip */}
          <div className="relative overflow-hidden rounded-xl border border-cacao-gold/20 bg-gradient-to-br from-cacao-gold/5 to-cacao-dark-secondary p-5">
            <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-cacao-gold/10 blur-2xl" />
            <div className="relative flex items-start gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-cacao-gold/20">
                <Zap className="h-5 w-5 text-cacao-gold" />
              </div>
              <div>
                <p className="font-semibold text-cacao-gold">Pro Tip</p>
                <p className="mt-1.5 text-sm text-cacao-muted leading-relaxed">
                  {stats.contacted === 0
                    ? 'Send your first email today! Consistency is key to building pipeline.'
                    : stats.replyRate < 10
                    ? 'Try personalizing your subject lines to improve open rates.'
                    : stats.replyRate < 20
                    ? 'Great progress! Focus on warm leads for higher conversion.'
                    : "You're crushing it! Consider increasing your daily outreach volume."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
